import { MERGE_TAG_KEYS, formatMergeTag, mergeTagLabel, type MergeTagKey } from "../domain/merge-tags";

export type TEmailBuilderMergeTag = MergeTagKey;

export interface IEmailBuilderMergeTagToken {
  start: number;
  end: number;
  openingLength: 1 | 2;
  query: string;
  tokenText: string;
}

export interface IEmailBuilderMergeTagSuggestion {
  tag: TEmailBuilderMergeTag;
  label: string;
  insertion: string;
  typed: string;
  predicted: string;
}

function consumeEmailBuilderMergeTagSuffixAfterCaret(
  textAfterCaret: string,
  closingBraceCountBefore: number,
): number {
  if (closingBraceCountBefore >= 1) {
    return textAfterCaret.startsWith("}") ? 1 : 0;
  }

  // Only extend past the caret when a closing brace appears, so selecting a
  // suggestion while inside an existing tag replaces the whole tag without
  // eating trailing prose like `{{cus|world`.
  const match = textAfterCaret.match(/^([A-Za-z0-9._]*)\}{1,2}/);
  return match === null ? 0 : match[0].length;
}

export function findEmailBuilderMergeTagToken(
  textBeforeCaret: string,
  textAfterCaret: string = "",
): IEmailBuilderMergeTagToken | null {
  // Allow one trailing `}` so the menu stays open until both closing braces
  // are typed. A completed `{{tag}}` fails this pattern and returns null.
  const match = textBeforeCaret.match(/(\{\{?)([^}]*)(\}?)$/);
  if (match === null || match.index === undefined) {
    return null;
  }

  const opening = match[1];
  const query = match[2];
  const closingBefore = match[3];
  const tokenText = match[0];
  const openingLength = opening.length === 2 ? 2 : 1;
  const afterLength = consumeEmailBuilderMergeTagSuffixAfterCaret(
    textAfterCaret,
    closingBefore.length,
  );

  return {
    start: match.index,
    end: match.index + tokenText.length + afterLength,
    openingLength,
    query,
    tokenText,
  };
}

function rankEmailBuilderMergeTagMatches(
  query: string,
  tags: readonly TEmailBuilderMergeTag[] = MERGE_TAG_KEYS,
): TEmailBuilderMergeTag[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery === "") {
    return [...tags];
  }

  const queryTokens = normalizedQuery.split(/[.\s_]+/).filter(token => token.length > 0);

  const keyPrefix: TEmailBuilderMergeTag[] = [];
  const labelPrefix: TEmailBuilderMergeTag[] = [];
  const contained: TEmailBuilderMergeTag[] = [];
  const tokenMatched: TEmailBuilderMergeTag[] = [];

  for (const tag of tags) {
    const key = tag.toLowerCase();
    const labelValue = mergeTagLabel(tag);
    if (labelValue === undefined) {
      continue;
    }
    const label = labelValue.toLowerCase();
    const haystack = `${key} ${label}`;

    if (key.startsWith(normalizedQuery)) {
      keyPrefix.push(tag);
      continue;
    }

    if (label.startsWith(normalizedQuery)) {
      labelPrefix.push(tag);
      continue;
    }

    if (key.includes(normalizedQuery) || label.includes(normalizedQuery)) {
      contained.push(tag);
      continue;
    }

    if (
      queryTokens.length > 0 &&
      queryTokens.every(token => haystack.includes(token))
    ) {
      tokenMatched.push(tag);
    }
  }

  return [...keyPrefix, ...labelPrefix, ...contained, ...tokenMatched];
}

function splitEmailBuilderMergeTagSuggestionDisplay(
  tokenText: string,
  insertion: string,
): { typed: string; predicted: string } {
  if (tokenText.length > 0 && insertion.startsWith(tokenText)) {
    return {
      typed: tokenText,
      predicted: insertion.slice(tokenText.length),
    };
  }

  return {
    typed: "",
    predicted: insertion,
  };
}

export function buildEmailBuilderMergeTagSuggestions(
  token: IEmailBuilderMergeTagToken,
  tags: readonly TEmailBuilderMergeTag[] = MERGE_TAG_KEYS,
): IEmailBuilderMergeTagSuggestion[] {
  return rankEmailBuilderMergeTagMatches(token.query, tags).map(tag => {
    const insertion = formatMergeTag(tag);
    const { typed, predicted } = splitEmailBuilderMergeTagSuggestionDisplay(
      token.tokenText,
      insertion,
    );

    return {
      tag,
      label: mergeTagLabel(tag),
      insertion,
      typed,
      predicted,
    };
  });
}

export function moveEmailBuilderMergeTagActiveIndex(
  activeIndex: number,
  delta: number,
  count: number,
): number {
  if (count <= 0) {
    return 0;
  }

  return (activeIndex + delta + count * 10) % count;
}

export const EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_EDGE_GAP_PX = 8;
export const EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_CARET_GAP_PX = 4;
export const EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_PREFERRED_WIDTH_PX = 280;
export const EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_PREFERRED_MAX_HEIGHT_PX = 240;

export interface IEmailBuilderMergeTagAutocompletePosition {
  left: number;
  top: number;
  maxWidth: number;
  maxHeight: number;
}

export interface IEmailBuilderMergeTagAutocompleteSafeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function measureEmailBuilderMergeTagAutocompleteSafeBounds(
  viewport: { width: number; height: number } = {
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  },
  root: ParentNode | null = typeof document !== "undefined" ? document : null,
): IEmailBuilderMergeTagAutocompleteSafeBounds {
  const gap = EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_EDGE_GAP_PX;
  let left = gap;
  let right = Math.max(gap, viewport.width - gap);
  const top = gap;
  const bottom = Math.max(gap, viewport.height - gap);

  if (root === null) {
    return { left, top, right, bottom };
  }

  const rail = root.querySelector(".builder-toolbar-rail");
  if (rail instanceof HTMLElement) {
    const railRect = rail.getBoundingClientRect();
    if (railRect.width > 0) {
      left = Math.max(left, railRect.right + gap);
    }
  }

  root.querySelectorAll(".builder-toolbar-menu-panel.open").forEach(node => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    if (node.classList.contains("email-builder-settings-side-panel--right")) {
      return;
    }
    const panelRect = node.getBoundingClientRect();
    if (panelRect.width > 0) {
      left = Math.max(left, panelRect.right + gap);
    }
  });

  const settingsPanel = root.querySelector(
    ".email-builder-settings-side-panel--right.builder-toolbar-menu-panel.open",
  );
  if (settingsPanel instanceof HTMLElement) {
    const settingsRect = settingsPanel.getBoundingClientRect();
    if (settingsRect.width > 0) {
      right = Math.min(right, settingsRect.left - gap);
    }
  }

  if (right < left) {
    right = left;
  }

  return { left, top, right, bottom };
}

export function resolveEmailBuilderMergeTagContentBounds(
  editorElement: HTMLElement,
): IEmailBuilderMergeTagAutocompleteSafeBounds | undefined {
  const gap = EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_EDGE_GAP_PX;
  const canvas = editorElement.closest(".email-builder-canvas");
  const contentNode =
    canvas?.querySelector("[data-editor-content-bg]") ??
    canvas?.querySelector(".email-builder-canvas-frame");
  if (!(contentNode instanceof HTMLElement)) {
    return undefined;
  }
  const rect = contentNode.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return undefined;
  }
  return {
    left: rect.left + gap,
    top: rect.top + gap,
    right: rect.right - gap,
    bottom: rect.bottom - gap,
  };
}

function resolveEmailBuilderMergeTagAutocompleteLeft(args: {
  anchorLeft: number;
  anchorRight: number;
  maxWidth: number;
  bounds: IEmailBuilderMergeTagAutocompleteSafeBounds;
}): number {
  const minLeft = args.bounds.left;
  const maxLeft = args.bounds.right - args.maxWidth;
  if (maxLeft <= minLeft) {
    return minLeft;
  }

  const leftAligned = args.anchorLeft;
  const rightAligned = args.anchorRight - args.maxWidth;
  const leftAlignedFits =
    leftAligned >= minLeft && leftAligned + args.maxWidth <= args.bounds.right;
  const rightAlignedFits =
    rightAligned >= minLeft && rightAligned + args.maxWidth <= args.bounds.right;

  if (leftAlignedFits) {
    return leftAligned;
  }
  if (rightAlignedFits) {
    return rightAligned;
  }

  const spaceToRight = args.bounds.right - args.anchorLeft;
  const spaceToLeft = args.anchorRight - args.bounds.left;
  const preferredLeft =
    spaceToRight >= args.maxWidth || spaceToRight >= spaceToLeft
      ? leftAligned
      : rightAligned;

  return Math.max(minLeft, Math.min(preferredLeft, maxLeft));
}

export function resolveEmailBuilderMergeTagAutocompleteAnchor(args: {
  caretRect: { left: number; top: number; right: number; bottom: number; width: number; height: number };
  fallbackRect: { left: number; top: number; right: number; bottom: number };
  safeBounds?: IEmailBuilderMergeTagAutocompleteSafeBounds;
  contentBounds?: IEmailBuilderMergeTagAutocompleteSafeBounds;
  preferredWidth?: number;
  preferredMaxHeight?: number;
}): IEmailBuilderMergeTagAutocompletePosition {
  const caretIsEmpty = args.caretRect.width === 0 && args.caretRect.height === 0;
  const anchorLeft = caretIsEmpty ? args.fallbackRect.left : args.caretRect.left;
  const anchorRight = caretIsEmpty ? args.fallbackRect.right : args.caretRect.right;
  const anchorTop = caretIsEmpty ? args.fallbackRect.top : args.caretRect.top;
  const anchorBottom = caretIsEmpty ? args.fallbackRect.bottom : args.caretRect.bottom;

  let safeBounds =
    args.safeBounds ??
    measureEmailBuilderMergeTagAutocompleteSafeBounds({
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      height: typeof window !== "undefined" ? window.innerHeight : 0,
    });
  if (args.contentBounds) {
    const left = Math.max(safeBounds.left, args.contentBounds.left);
    const right = Math.min(safeBounds.right, args.contentBounds.right);
    if (right >= left) {
      safeBounds = { ...safeBounds, left, right };
    }
  }
  const preferredWidth =
    args.preferredWidth ?? EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_PREFERRED_WIDTH_PX;
  const preferredMaxHeight =
    args.preferredMaxHeight ?? EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_PREFERRED_MAX_HEIGHT_PX;
  const caretGap = EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_CARET_GAP_PX;

  const availableWidth = Math.max(0, safeBounds.right - safeBounds.left);
  const maxWidth = Math.min(preferredWidth, availableWidth);

  const left = resolveEmailBuilderMergeTagAutocompleteLeft({
    anchorLeft,
    anchorRight,
    maxWidth,
    bounds: safeBounds,
  });

  const spaceBelow = Math.max(0, safeBounds.bottom - (anchorBottom + caretGap));
  const spaceAbove = Math.max(0, anchorTop - caretGap - safeBounds.top);
  const placeBelow = spaceBelow >= Math.min(preferredMaxHeight, 120) || spaceBelow >= spaceAbove;

  if (placeBelow) {
    return {
      left,
      top: anchorBottom + caretGap,
      maxWidth,
      maxHeight: Math.min(preferredMaxHeight, spaceBelow),
    };
  }

  const maxHeight = Math.min(preferredMaxHeight, spaceAbove);
  return {
    left,
    top: Math.max(safeBounds.top, anchorTop - caretGap - maxHeight),
    maxWidth,
    maxHeight,
  };
}
